import React, { createContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
	syncSingleConfig,
	syncBulkConfig,
	createConfig,
	updateConfig,
	getPlaylistConfig
} from '../../api/playlists';
import { toast } from 'react-toastify';
import { PLAYLIST_MODES } from '../../core/constants';
import { UnitsContext } from '../UnitsContext';
import { cleanPlaylistConfigData } from '../../utilities/pageLevelVideosConfigCheck';
import { validatePlaylistCtas } from '../../utilities/pageLevelVideoCta';
import { getProcessedConfigDataFromMapping, getNewPageObject, getNewVideoObject } from './utils';

/**
 * Context for managing page-level video configuration.
 */
const PageLevelVideoConfigContext = createContext();

const PageLevelVideoConfigContextProvider = ({ children }) => {
	// State variables for managing page-level video configuration
	const [playlistConfigData, setPlaylistConfigData] = useState([]);
	const [playlistConfigError, setPlaylistConfigError] = useState(false);
	const [playlistConfigNotFound, setPlaylistConfigNotFound] = useState(false);
	const [playlistConfigLoading, setPlaylistConfigLoading] = useState(true);
	const [playlistConfigMode, setPlaylistConfigMode] = useState(
		PLAYLIST_MODES.SITE_LEVEL_UPDATE_PAGE_VIDEOS
	);
	const [playlistConfigPage, setPlaylistConfigPage] = React.useState(1);

	// Other context and routing variables
	const { siteId } = useParams();
	const { siteRawConfig } = useContext(UnitsContext);

	// Effect to fetch and set the page-level video config from mapping doc in couchbase

	useEffect(() => {
		const setPageLevelVideoConfig = async () => {
			try {
				const response = await getPlaylistConfig(siteId);
				if (response.status === 200) {
					const mapping = response.data.data.mapping;
					const configArr = getProcessedConfigDataFromMapping(mapping);
					setPlaylistConfigLoading(false);
					setPlaylistConfigData(configArr);
				} else {
					setPlaylistConfigError(true);
				}
			} catch (err) {
				setPlaylistConfigLoading(false);
				if (
					err.response.status === 500 &&
					err.response.data.data.message === 'The key does not exist on the server'
				) {
					setPlaylistConfigNotFound(true);
				} else {
					setPlaylistConfigError(true);
				}
			}
		};
		// We only send a request if there is a siteId else it is an error
		// This will happen during creating site config as well
		if (siteId) setPageLevelVideoConfig();
		else {
			setPlaylistConfigLoading(false);
			setPlaylistConfigError(true);
		}
	}, []);

	/**
	 * Deletes a page from the playlist configuration data.
	 * @param {string} id - ID of the page to be deleted.
	 */
	const deletePlaylistConfigPage = (id) => {
		// We have to change the page of table if we are deleting the current page
		setPlaylistConfigPage((page) => {
			if (page !== 1) return page - 1;
			return page;
		});

		// Deleting page from the config
		setPlaylistConfigData((prevData) => {
			const clonedPrevData = structuredClone(prevData);
			return clonedPrevData.filter((config) => config.id !== id);
		});
	};

	/**
	 * Saves the playlist configuration pages.
	 * Sends the payload depending upon the current mode in playlist
	 * @returns {Promise} A promise that resolves after saving the configuration.
	 */
	const savePlaylistConfigPages = async () => {
		try {
			const siteDomain = siteRawConfig?.siteDomain || '';
			// We first validate if the config is correct. Then only we proceed
			// We clean playlist Config from extra data
			const cleanedPlaylistConfigData = cleanPlaylistConfigData(playlistConfigData);
			validatePlaylistCtas(cleanedPlaylistConfigData);
			const payload = {
				data: cleanedPlaylistConfigData,
				siteId,
				siteDomain
			};
			const { data } =
				playlistConfigMode === PLAYLIST_MODES.CREATE_PAGE_VIDEOS
					? await createConfig(payload)
					: await updateConfig(payload);

			if (playlistConfigMode === PLAYLIST_MODES.CREATE_PAGE_VIDEOS) {
				setPlaylistConfigNotFound(false);
				setPlaylistConfigMode(PLAYLIST_MODES.UPDATE_PAGE_VIDEOS);
			}

			if (!data?.success) throw Error(data?.data?.message);
			toast.success(data?.data?.message, { position: toast.POSITION.BOTTOM_RIGHT });
			setPlaylistConfigData(cleanedPlaylistConfigData);
			return data;
		} catch (err) {
			const requestError = err?.response?.data?.data?.message;
			toast.error(requestError || err?.message, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	/**
	 * Adds a video to the playlist configuration for a specific page.
	 * @param {string} id - ID of the page where the video will be added.
	 */
	const handlePlaylistConfigAddVideo = (id) => {
		setPlaylistConfigData((prevData) => {
			const clonedPrevData = structuredClone(prevData);
			const updatedData = clonedPrevData.map((config) => {
				if (config.id === id) {
					const newVideo = getNewVideoObject();
					config.videos.push(newVideo);
					return config;
				}
				return config;
			});
			return updatedData;
		});
	};

	/**
	 * Adds a new page to the playlist configuration.
	 */
	const handlePlaylistConfigAddPage = () => {
		setPlaylistConfigData((prevData) => {
			const clonedPrevData = structuredClone(prevData);
			const newPage = getNewPageObject();
			clonedPrevData.push(newPage);
			return clonedPrevData;
		});
	};

	/**
	 * Removes a video from the playlist configuration of a specific page.
	 * @param {string} configId - ID of the page's configuration (uuid value that we generate and
	 * set as id of the config).
	 * @param {number} videoIndex - Index of the video to be removed.
	 */
	const handlePlaylistConfigRemoveVideo = (configId, videoIndex) => {
		setPlaylistConfigData((prevConfigData) => {
			const clonedPrevData = structuredClone(prevConfigData);
			const updatedData = clonedPrevData.map((prevConfig) => {
				if (prevConfig.id === configId) {
					prevConfig.videos = prevConfig.videos.filter((_, index) => index !== videoIndex);
					return prevConfig;
				}
				return prevConfig;
			});
			return updatedData;
		});
	};

	/**
	 * Initiates a single page sync of playlist configuration.
	 * It fetches the doc from couchbase and syncs the page url matching our
	 * pageUrl parameter
	 * @param {string} pageUrl - URL of the page to be synchronized.
	 */
	const handlePlaylistConfigSinglePageSync = async (pageUrl) => {
		try {
			const siteDomain = siteRawConfig?.siteDomain || '';
			const payload = {
				pageUrl,
				siteId,
				siteDomain
			};
			const response = await syncSingleConfig(payload);
			toast.success('Page Sync successful !!', { position: toast.POSITION.BOTTOM_RIGHT });
		} catch (err) {
			const requestError = err?.response?.data?.data?.message;
			toast.error(requestError || err?.message, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	/**
	 * Initiates a bulk sync of playlist configuration for all pages in the site
	 * It fetches the doc from couchbase and syncs all the pages one by one
	 */
	const handlePlaylistConfigBulkPageSync = async () => {
		try {
			const siteDomain = siteRawConfig?.siteDomain || '';
			const payload = {
				siteId,
				siteDomain
			};
			const response = await syncBulkConfig(payload);
			toast.success('Bulk Page Sync successful !!', { position: toast.POSITION.BOTTOM_RIGHT });
		} catch (err) {
			const requestError = err?.response?.data?.data?.message;
			toast.error(requestError || err?.message, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	return (
		<PageLevelVideoConfigContext.Provider
			value={{
				playlistConfigData,
				handlePlaylistConfigAddPage,
				playlistConfigMode,
				setPlaylistConfigMode,
				deletePlaylistConfigPage,
				savePlaylistConfigPages,
				siteId,
				setPlaylistConfigData,
				handlePlaylistConfigAddVideo,
				handlePlaylistConfigRemoveVideo,
				playlistConfigLoading,
				playlistConfigNotFound,
				playlistConfigError,
				setPlaylistConfigError,
				setPlaylistConfigNotFound,
				handlePlaylistConfigSinglePageSync,
				handlePlaylistConfigBulkPageSync,
				playlistConfigPage,
				setPlaylistConfigPage
			}}
		>
			{children}
		</PageLevelVideoConfigContext.Provider>
	);
};

const usePageLevelVideoConfigContext = () => useContext(PageLevelVideoConfigContext);

export { PageLevelVideoConfigContextProvider, usePageLevelVideoConfigContext };
